import * as React from "react";
import { connect } from "react-redux";
import type { Dispatch } from "redux";
import moment from "moment";

import Authorization from "@/modules/shared/Authorization";
import { DragHandle } from "@/modules/shared/ui/DraggableList";
import type { SortableDragHandleProps } from "@/modules/shared/ui/DraggableList";
import * as t from "@/lib/backend/manufacturing2.generated.types";
import { translate } from "@/modules/shared/IntlProviderWrapper";
import { niceDate, niceNumber, niceDateTime, dateFromShiftTime, addHours, removeMarkdownFromText } from "@/lib/util";
import { RED_COLOR, ORANGE_COLOR, GREEN_COLOR } from "@/modules/pg/shared/planningTable/colors";
import { isWarningInsight, isErrorInsight } from "@/lib/insights";
import { PERMISSION_NAMES } from "@/lib/auth";
import type { ITags } from "@/lib/models";
import { getLineGroupTagBool, getLineTitle } from "@/lib/businessLogic";
import { UPDATE_LINE_ORDER_ACTION_NAME } from "@/modules/pg/shared/planningTable/reducers/linesOrders";
import {
    LinesOrdersLogic,
    ReduxFilters,
    ReportLogic,
    RecommendPlanLogic,
} from "@/modules/pg/shared/planningTable/reducers/logic/businessLogic";
import translatedMaterialTitle from "@/modules/shared/translatedMaterialTitle";
import { INSIGHT_DIFF_TYPE } from "@/modules/pg/shared/planningTable/reducers/logic/businessLogic";
import {
    LINE_GROUP_TAGS,
    INSIGHT_TYPES,
    ORDER_TAGS,
    ORDER_TAGS_ACCESS,
    LINE_TAGS_ACCESS,
    INSIGHT_TAGS_ACCESS,
} from "@/lib/ManufacturingTags.generated";
import { PLANNING_EXPLANATION_TYPE, ORDER_TYPE } from "@/lib/ManufacturingConsts.generated";
import {
    SET_ADVANCED_ORDER_MENU,
    SET_HIGHLIGHTED_MOVE_ORDER,
    SET_CAMPAIGN_REFERENCE_KEY,
} from "@/modules/pg/shared/planningTable/reducers/planningTable";
import OrderCommentTextArea from "../order/OrderCommentTextArea";
import ConnectedOrderInlineComment from "../order/OrderInlineComment";

import { ScrollHandler } from "@/modules/pg/shared/planningTable/ganttChart/scrollHandler";
import OrderBadges from "@/modules/pg/shared/OrderBadges";
import ToolSetupLabel from "./ToolSetupLabel";
import PlanningTableColumns from "@/modules/pg/shared/planningTable/planningDetails/PlanningTableColumns";
import ConditionalStyles from "./conditionalStyles";

import type {
    SetAdvancedOrderMenuAction,
    SetHighlightedMoveOrder,
    SetCampaignReferenceKey,
} from "@/modules/pg/shared/planningTable/reducers/planningTable";
import type {
    IOrderProducedModelEx,
    UpdateLineOrderAction,
} from "@/modules/pg/shared/planningTable/reducers/linesOrders";
import type { ReduxState } from "@/modules/pg/shared/planningTable/reducers/reducers";
import type { SetRescheduleOrderBundleAction } from "@/modules/pg/shared/planningTable/reducers/common";
import type { PlanningTableColumnId, PlanningTableColumn } from "@/modules/pg/shared/viewsCommon";
import { CAMPAIGN_GROUP_REF_COLOR, CAMPAIGN_GROUP_TO_COLOR } from "@/modules/pg/shared/viewsCommon";

import type { CampaignGroup } from "@/modules/pg/shared/planningTable/reducers/linesOrders/orderCampaign";
import { Cell, type TextAlign } from "react-table";

const INSIGHT_COLORS = {
    default: "#f9c9c9",
    solved: "#70ad4721",
    [INSIGHT_TYPES.tool_setup]: "#ecebeb",
    [INSIGHT_TYPES.freeze_order_move]: "#c9dcf9",
    [INSIGHT_TYPES.operation_no_input_material]: "#ffb68f",
    [INSIGHT_TYPES.partially_scheduled]: "#dd8fff",
} as const;

type InsightColor = (typeof INSIGHT_COLORS)[keyof typeof INSIGHT_COLORS];

const getInsightDescriptionColor = (insight: t.IEventDataEx, is_solved: boolean): InsightColor => {
    if (is_solved) {
        return INSIGHT_COLORS.solved;
    }
    return INSIGHT_COLORS[insight.type as keyof typeof INSIGHT_COLORS] ?? INSIGHT_COLORS.default;
};

type InsightDescriptionExtraData = {
    extra_data: any;
};

const InsightDescriptionOpNoInputMaterial = (props: InsightDescriptionExtraData) => {
    const line_title = getLineTitle(props.extra_data.line_uuids[0]);

    return (
        <>
            <div className="title">
                {translate(
                    "Manufacturing.Planning.insight_operation_no_input_material",
                    "Order not covered with material"
                )}{" "}
                ({line_title}, {props.extra_data.order_external_id})
            </div>
            <table className="w-100">
                <tbody>
                    <tr>
                        <th>{translate("common.date", "Date")}</th>
                        <th>{translate("common.material", "Material")}</th>
                        <th>{translate("common.quantity", "Quantity")}</th>
                        <th>{translate("common.missing", "Missing")}</th>
                    </tr>
                    {props.extra_data.materials.map((m, idx) => (
                        <tr key={idx}>
                            <td>{niceDate(new Date(m.shift_time.shift_date))}</td>
                            <td>
                                {m.material_title} ({m.material_external_id})
                            </td>
                            <td>{Math.round(m.required_quantity)}</td>
                            <td>{Math.round(m.missing_quantity_external)}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </>
    );
};

const InsightDescriptionOpDelayDownstream = (props: InsightDescriptionExtraData) => {
    const title =
        props.extra_data.delayed_operations.length > 0 && props.extra_data.delayed_requirements.length > 0
            ? `${translate("common.orders", "Orders")} / ${translate("common.requirements", "Requirements")}`
            : props.extra_data.delayed_operations.length > 0
              ? translate("common.orders", "Orders")
              : translate("common.requirements", "Requirements");

    return (
        <>
            <div className="insight-message">
                {translate("Manufacturing.Planning.insight_operation_delay_downstream", "Order will cause delay")}
            </div>
            <div className="title">
                {props.extra_data.material_external_id}&nbsp;-&nbsp;
                {translatedMaterialTitle({
                    material_external_id: props.extra_data.material_external_id,
                    material_title: props.extra_data.material_title,
                })}
            </div>
            <table className="w-100">
                <tbody>
                    <tr>
                        <th>{translate("common.date", "Date")}</th>
                        <th>{title}</th>
                        <th>{translate("common.quantity", "Quantity")}</th>
                        <th>{translate("common.missing", "Missing")}</th>
                    </tr>
                    {props.extra_data.delayed_operations.map((o, idx) => (
                        <tr key={`o${idx}`}>
                            <td>{niceDate(new Date(o.shift_time.shift_date))}</td>
                            <td>{o.order_external_id}</td>
                            <td>{Math.round(o.required_quantity)}</td>
                            <td>{Math.round(o.missing_quantity)}</td>
                        </tr>
                    ))}
                    {props.extra_data.delayed_requirements.map((r, idx) => (
                        <tr key={`r${idx}`}>
                            <td>{niceDate(new Date(r.shift_time.shift_date))}</td>
                            <td>{r.required_external_id}</td>
                            <td>{Math.round(r.required_quantity)}</td>
                            <td>{Math.round(r.missing_quantity)}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </>
    );
};

type InsightsDescriptionDiffsProps = {
    title: string;
    insights: Map<string, t.IEventDataEx[]>;
    selected_order_uuid: string;
    onClickInfo: (order_external_id: string) => (e: React.MouseEvent<HTMLElement>) => void;
    onClickOrder: (order_uuid: string) => () => Promise<void>;
};

export const InsightsDescriptionDiffs = (props: InsightsDescriptionDiffsProps) => {
    const renderInsight = (diff_type: string, insight: t.IEventDataEx, idx: number) => {
        let style: React.CSSProperties = {};

        const is_solved = diff_type === INSIGHT_DIFF_TYPE.solved;
        const insightColor = getInsightDescriptionColor(insight, is_solved);

        const order_uuid = ReportLogic.getOrderUuidFromInsight(insight);
        const order_extra_id = ReportLogic.getOrderExternalIdFromInsight(insight);

        if (props.selected_order_uuid === order_uuid) {
            style.border = "1px solid black";
        }

        const insight_order_uuid = ReportLogic.getOrderUuidFromInsight(insight);
        return (
            <div
                style={{ display: "flex", alignItems: "center", cursor: "pointer" }}
                onClick={props.onClickOrder(order_uuid)}
                key={idx}
            >
                <div style={{ display: "flex", alignItems: "stretch", cursor: "pointer", width: "100%" }}>
                    <div
                        order-uuid={insight_order_uuid}
                        className="planning-table-insight-description"
                        style={{ ...style, "--insight-color": insightColor } as React.CSSProperties}
                        key={insight.uuid}
                    >
                        {insight.type === INSIGHT_TYPES.tool_setup ? (
                            <span>
                                {insight.tags.line_title} - {removeMarkdownFromText(insight.title, ["**"])}
                            </span>
                        ) : insight.type === INSIGHT_TYPES.operation_no_input_material ? (
                            <InsightDescriptionOpNoInputMaterial extra_data={insight.extra_data} />
                        ) : insight.type === INSIGHT_TYPES.operation_delay_downstream ? (
                            <InsightDescriptionOpDelayDownstream extra_data={insight.extra_data} />
                        ) : (
                            <span>
                                {insight.tags.line_title} - {removeMarkdownFromText(insight.title, ["**"])}
                            </span>
                        )}
                    </div>
                    <div
                        style={{
                            paddingLeft: "5px",
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "space-evenly",
                        }}
                    >
                        <span className="info-icon" onClick={props.onClickInfo(order_extra_id)}>
                            <i className="fas fa-info"></i>
                        </span>
                        {is_solved ? (
                            <i className="fas fa-check" style={{ fontSize: "15px", color: "#70AD47" }}></i>
                        ) : (
                            <i className="fas fa-times" style={{ fontSize: "20px", color: "#FF0000" }}></i>
                        )}
                        <div style={{ height: "15px" }}></div>
                    </div>
                </div>
            </div>
        );
    };

    const insights_elements: React.ReactNode[] = [];
    for (const [diff_type, values] of props.insights.entries()) {
        insights_elements.push(values.map((i, idx) => renderInsight(diff_type, i, idx)));
    }

    return (
        <div className="insights-descriptions-container" style={{ marginTop: "unset" }} title={props.title}>
            {insights_elements}
        </div>
    );
};

type InsightsDescriptionProps = {
    insights: t.IEventDataEx[];
};

// Remove markdown from title and description since it's only used on order tooltips
export const InsightsDescription = (props: InsightsDescriptionProps) => {
    return (
        <div className="insights-descriptions-container">
            {props.insights.map((i, idx) => {
                const insightColor = getInsightDescriptionColor(i, false);

                if (i.type === INSIGHT_TYPES.tool_setup) {
                    return (
                        <React.Fragment key={idx}>
                            <div
                                className="planning-table-insight-description"
                                style={{ "--insight-color": insightColor } as React.CSSProperties}
                                key={idx}
                            >
                                <span>
                                    <b>{removeMarkdownFromText(i.title, ["**"])}</b>
                                </span>
                            </div>
                            {idx !== props.insights.length - 1 && (
                                <hr style={{ marginTop: "unset", marginBottom: "unset" }} />
                            )}
                        </React.Fragment>
                    );
                } else if (i.type === INSIGHT_TYPES.operation_no_input_material) {
                    return (
                        <React.Fragment key={idx}>
                            <div
                                className="planning-table-insight-description"
                                style={{ "--insight-color": insightColor } as React.CSSProperties}
                                key={idx}
                            >
                                <InsightDescriptionOpNoInputMaterial extra_data={i.extra_data} />
                            </div>
                            {idx !== props.insights.length - 1 && (
                                <hr style={{ marginTop: "unset", marginBottom: "unset" }} />
                            )}
                        </React.Fragment>
                    );
                } else if (i.type === INSIGHT_TYPES.operation_delay_downstream) {
                    return (
                        <React.Fragment key={idx}>
                            <div
                                className="planning-table-insight-description"
                                style={{ "--insight-color": insightColor } as React.CSSProperties}
                                key={idx}
                            >
                                <InsightDescriptionOpDelayDownstream extra_data={i.extra_data} />
                            </div>
                            {idx !== props.insights.length - 1 && (
                                <hr style={{ marginTop: "unset", marginBottom: "unset" }} />
                            )}
                        </React.Fragment>
                    );
                }

                return (
                    <React.Fragment key={idx}>
                        <div
                            className="planning-table-insight-description"
                            style={{ "--insight-color": insightColor } as React.CSSProperties}
                            key={idx}
                        >
                            <span>
                                <b>{removeMarkdownFromText(i.title, ["**"])}</b>{" "}
                                {removeMarkdownFromText(i.description, ["**"])}
                            </span>
                        </div>
                        {idx !== props.insights.length - 1 && (
                            <hr style={{ marginTop: "unset", marginBottom: "unset" }} />
                        )}
                    </React.Fragment>
                );
            })}
        </div>
    );
};

type ExplanationDescriptionProps = {
    explanation: t.IPlanExplanation;
};

export const ExplanationDescription = (props: ExplanationDescriptionProps) => {
    const { explanation } = props;
    const text = RecommendPlanLogic.getExplanationText(explanation);

    return text == null ? null : (
        <div
            className="planning-table-insight-description"
            style={{ "--insight-color": "#f7f7f7" } as React.CSSProperties}
        >
            <span title={JSON.stringify(explanation.extra_data)}>{text}</span>
        </div>
    );
};

export type OrderObjGui = {
    capacity_factor: number;
    capacity_factor_str: string;
    capacity_overriden: boolean;
    earliest_end: string;
    earliest_start: string;
    enabled: boolean;
    skip_sim: boolean;
    external_id: string;
    has_production?: boolean;
    initial_earliest_end: string;
    initial_earliest_start: string;
    material_external_id: string;
    material_title: string;
    measurement_unit: string;
    order_type: string;
    process_uuid: string;
    quantity_total: number;
    quantity_total_str: string;
    sequence_weight: number;
    sim_ignore_earliest_start: boolean;
    uuid: string;
    process_num: string;
    campaign_key?: string;
    campaign_group?: CampaignGroup;
};

const getSelectValues = (sim_ignore_earliest_start: boolean | null, earliest_start: string) => {
    return [
        {
            value: true,
            label: "24 / 7",
        },
        {
            value: false,
            label: earliest_start,
        },
    ];
};

export const canIgnoreEarliestStart = (line_tags: ITags): boolean => {
    return !LINE_TAGS_ACCESS.overlapping_orders_line(line_tags) || LINE_TAGS_ACCESS.sublines_number(line_tags) > 1;
};

// EditButton

type EditButtonActionTypes = SetAdvancedOrderMenuAction;
type EditButtonOwnProps = { order_uuid: string };
type EditButtonProps = EditButtonOwnProps & EditButtonReduxProps;

class EditButton extends React.Component<EditButtonProps> {
    onClick = (e: React.MouseEvent<HTMLElement>) => {
        e.stopPropagation();
        const currentTarget = e.currentTarget;
        if (currentTarget instanceof Element) {
            this.props.reduxDispatch({
                type: SET_ADVANCED_ORDER_MENU,
                data: {
                    order_uuid: this.props.order_uuid,
                    click_target: currentTarget,
                    event: e,
                },
            });
        }
    };

    render() {
        return (
            <div>
                <i className="far fa-edit icon-interactive" onClick={this.onClick}></i>
            </div>
        );
    }
}

const editButtonMapDispatch = (dispatch: Dispatch<EditButtonActionTypes>) => ({
    reduxDispatch: dispatch,
});

type EditButtonReduxProps = ReturnType<typeof editButtonMapDispatch>;

const ConnectedEditButton = connect<{}, ReturnType<typeof editButtonMapDispatch>, EditButtonOwnProps, ReduxState>(
    null,
    editButtonMapDispatch
)(EditButton);

// OrderDetails

type OrderDetailsCellProps = {
    row_class: string;
    index: number;
    selected_order: OrderObjGui;
    handleSelectChange: (earliest_start: { value: boolean }) => void;
    line_uuid: string;
    insights: t.IEventDataEx[];
    explanation?: t.IPlanExplanation;
    line_group_uuid: string;
    is_unscheduled: boolean;
    is_readonly?: boolean;
    can_ignore_earliest_start: boolean;
    start_time_sim?: number;
};
/*
    Please read - to enable edit on a new editable component, please add a new condition inside onDragOrder
*/
const OrderDetailsCell = (props: OrderDetailsCellProps) => {
    const { handleSelectChange, selected_order, insights, explanation, line_group_uuid } = props;

    const select_values = getSelectValues(selected_order.sim_ignore_earliest_start, selected_order.earliest_start);
    const resolved_explanation = explanation
        ? explanation
        : {
              operation_uuid: selected_order.uuid,
              type: PLANNING_EXPLANATION_TYPE.not_planned,
              possible_prod_vers: [],
              extra_data: {},
          };
    const show_explanation = getLineGroupTagBool(line_group_uuid, LINE_GROUP_TAGS.automatic_scheduling, false);
    const start_time_sim = props.start_time_sim;
    return (
        <div className="editable-cell" onClick={event => event.stopPropagation()}>
            {props.is_unscheduled && (
                <div className="editable-cell__from">
                    {translate("OrderTableProduction.earliest_start", "ERP start")} : {selected_order.earliest_start}
                </div>
            )}
            {!props.is_unscheduled && start_time_sim && (
                <div className="bold w-100 mb-2">
                    <span>{translate("OrderTableProduction.time_start_current", "Current start time")}:&nbsp;</span>
                    <span>{niceDateTime(start_time_sim)}</span>
                </div>
            )}
            {!props.is_unscheduled && (
                <div className="editable-cell__from">
                    <span className="label">{translate("Manufacturing.Planning.from", "From")}:</span> <br />
                    <div className="value">
                        <Authorization.Select
                            permission={PERMISSION_NAMES.OrdersAndShiftsEdit}
                            name="sim_ignore_earliest_start"
                            value={select_values.find(el => el.value === selected_order.sim_ignore_earliest_start)}
                            onChange={object => {
                                handleSelectChange(object);
                            }}
                            options={getSelectValues(
                                selected_order.sim_ignore_earliest_start,
                                selected_order.earliest_start
                            )}
                            inputId={"earliest_start_select"}
                            disabled={props.is_readonly === true || !props.can_ignore_earliest_start}
                        />
                    </div>
                </div>
            )}
            <InsightsDescription insights={insights} />
            {show_explanation && <ExplanationDescription explanation={resolved_explanation} />}
        </div>
    );
};

// Order

type ActionTypes =
    | SetAdvancedOrderMenuAction
    | UpdateLineOrderAction
    | SetHighlightedMoveOrder
    | SetCampaignReferenceKey
    | SetRescheduleOrderBundleAction;

type OwnProps = {
    sortIndex: number;
    order_uuid: string;
    line_uuid: string;
    columns: PlanningTableColumn[];
    campaign_group?: CampaignGroup;
    enabled_edit?: boolean;
    plant_uuid?: string;
    is_unscheduled?: boolean;
    capacity_factor?: number;
    /** Pass `{}` for visual-only handles (e.g. drag overlay); omit when not draggable. */
    dragHandleProps?: SortableDragHandleProps | null;
    draw_split_line?: boolean;
};

type Props = OwnProps & ReduxProps;

type State = {
    order_gui?: OrderObjGui;
};

type FilterInsightsArgs = {
    include?: string[];
    exclude?: string[];
    insights: t.IEventDataEx[];
};

class Order extends React.Component<Props, State> {
    static defaultProps: Partial<Props>;

    myRef = React.createRef<HTMLTableRowElement>();
    highlightTimeout?: ReturnType<typeof setTimeout>;

    state: State = {};

    makeRenderCell = (column_id: PlanningTableColumnId): Cell<OrderObjGui> => {
        return {
            advanced_menu: () => null,
            workorder: this.renderWorkorderCell,
            material: this.renderMaterialCell,
            material_id: this.renderMaterialIdCell,
            material_title: this.renderMaterialTitleCell,
            material_deadline: this.renderMaterialDeadlineCell,
            material_tool_setup: this.renderMaterialToolSetupCell,
            comment_inline: this.renderInlineCommentCell,
            unit: this.renderUnitCell,
            planned_order_end_date: this.renderPlannedOrderEndDateCell,
            bom_availability_days: this.renderBomAvailabilityDaysCell,
            required_deadline_diff: this.renderRequiredDeadlineDiffCell,
            editorder: this.renderEditOrderCell,
            moveorder_start: this.renderMoveOrderCell,
            moveorder_end: this.renderMoveOrderCell,
            toolbar: () => null,
        }[column_id];
    };

    componentDidMount() {
        this.updateOrder();
    }

    componentDidUpdate(prev_props: Props) {
        if (
            prev_props.quantity_total !== this.props.quantity_total ||
            prev_props.is_unscheduled !== this.props.is_unscheduled
        ) {
            this.updateOrder();
            return;
        }
        if (prev_props.is_rescheduled !== this.props.is_rescheduled) {
            const is_locked = ReduxFilters.isLineLocked();
            const ref = this.myRef?.current;
            if (ref && is_locked && this.props.is_rescheduled) {
                setTimeout(() => {
                    ref.scrollIntoView({ block: "center" });
                }, 200);
                return;
            }
        }

        if (prev_props.is_rescheduled !== this.props.is_rescheduled && this.props.is_rescheduled) {
            if (this.myRef?.current) {
                this.myRef.current.scrollIntoView({ block: "center" });
                return;
            }
        }

        if (prev_props.order != undefined && this.props.order != undefined) {
            if (prev_props.order.uuid !== this.props.order.uuid) {
                this.updateOrder();
            }
        } else if (prev_props.order != undefined || this.props.order != undefined) {
            this.updateOrder();
        }
    }

    updateOrder = () => {
        if (this.props.order != undefined) {
            const order_gui = this.createOrderObjGui(this.props.order);

            const planning_table_columns = PlanningTableColumns.getInstance();
            for (const custom_id of planning_table_columns.getCustomColumnIds()) {
                // TODO: ORDER_TAGS_ACCESS?
                order_gui[custom_id] = planning_table_columns.format(this.props.order.tags[custom_id] ?? "", custom_id);
            }

            this.setState({ order_gui });
        } else {
            this.setState({ order_gui: undefined });
        }
    };

    onClick = async (e: React.MouseEvent<HTMLTableRowElement>) => {
        if (!this.props.order || this.props.report_loading) return;

        e.preventDefault();
        e.stopPropagation();

        if (!this.props.is_rescheduled) {
            ReduxFilters.setRescheduleOrderUuid(
                this.props.reduxDispatch,
                this.props.order.uuid,
                "line_order_list",
                this.props.is_filter_locked
            );
        }
        ScrollHandler.scrollToOrder(this.props.order.uuid);
    };

    handleSelectChange = (earliest_start: { value: boolean }) => {
        const order = this.props.order;
        if (!order) return;

        const sim_ignore_earliest_start = earliest_start.value;

        if (order.sim_ignore_earliest_start !== sim_ignore_earliest_start) {
            order.sim_ignore_earliest_start = sim_ignore_earliest_start;

            this.props.reduxDispatch({
                type: UPDATE_LINE_ORDER_ACTION_NAME,
                data: {
                    line_uuid: this.props.line_uuid,
                    order_uuid: this.props.order_uuid,
                    order,
                },
            });
        }
    };

    createOrderObjGui(input: IOrderProducedModelEx): OrderObjGui {
        const digits = input.measurement_unit === "KG" ? 3 : 0;

        return {
            capacity_factor: input.override_capacity_factor || input.capacity_factor,
            capacity_factor_str: niceNumber(100 * (input.override_capacity_factor || input.capacity_factor), 0),
            capacity_overriden: input.override_capacity_factor !== null,
            earliest_end: niceDate(new Date(input.earliest_end)),
            earliest_start: niceDate(new Date(input.earliest_start)),
            enabled: !input.skip_sim,
            skip_sim: input.skip_sim,
            external_id: input.external_id,
            has_production: input.has_production,
            initial_earliest_end: niceDate(new Date(input.initial_earliest_end)),
            initial_earliest_start: niceDate(new Date(input.initial_earliest_start)),
            material_external_id: input.material_external_id,
            material_title: input.material_title,
            measurement_unit: input.measurement_unit && input.measurement_unit.trim().toUpperCase(),
            order_type: input.order_type,
            process_uuid: input.process_uuid,
            quantity_total: input.quantity_total,
            quantity_total_str: niceNumber(input.quantity_total, digits),
            sequence_weight: input.sequence_weight,
            sim_ignore_earliest_start: input.sim_ignore_earliest_start,
            uuid: input.uuid,
            process_num: input.process_num,
            campaign_key: input.tags.campaign_key,
            campaign_group: input.campaign_group,
        };
    }

    getIsEditable = () => {
        return this.props.enabled_edit && !this.props.show_unscheduled_orders;
    };

    filterInsights = (args: FilterInsightsArgs): t.IEventDataEx[] => {
        const { include, exclude, insights } = args;

        if (include && include.length > 0) {
            return insights.filter(i => include.includes(i.type));
        }
        if (exclude && exclude.length > 0) {
            return insights.filter(i => !exclude.includes(i.type));
        }

        return insights;
    };

    getFrozen = (order: IOrderProducedModelEx) => {
        if (!order) {
            return null;
        }
        const is_frozen = order.tags[ORDER_TAGS.insight_freeze_order] === "true";

        if (is_frozen) {
            return <i className="fas fa-lock" style={{ marginRight: "2px" }}></i>;
        }

        return null;
    };

    getFixedLine = (order: IOrderProducedModelEx) => {
        if (!order) {
            return null;
        }

        const is_fixed_line = order.tags[ORDER_TAGS.fix_line] !== undefined;

        if (is_fixed_line) {
            return <i className="fas fa-thumbtack" style={{ marginRight: "2px" }}></i>;
        }

        return null;
    };

    onClickHighlight = (e: React.MouseEvent<HTMLElement>) => {
        if (!this.props.order) return;

        ScrollHandler.scrollToOrder(this.props.order.uuid);
        this.props.reduxDispatch({
            type: SET_HIGHLIGHTED_MOVE_ORDER,
            data: this.props.highlighted_move_order_uuid === this.props.order.uuid ? null : this.props.order.uuid,
        });
        this.props.reduxDispatch({
            type: SET_CAMPAIGN_REFERENCE_KEY,
            data:
                this.props.highlighted_move_order_uuid === this.props.order.uuid
                    ? null
                    : this.props.order.tags.campaign_key,
        });
        e.preventDefault();
        e.stopPropagation();
    };

    getRowBackgroundColor(): string {
        if (this.props.highlighted_move_order_uuid === this.state.order_gui?.uuid) {
            return CAMPAIGN_GROUP_REF_COLOR;
        }

        const value = this.props.campaign_group?.value;
        return CAMPAIGN_GROUP_TO_COLOR[value ?? ""] ?? "";
    }

    getInsightColor(): string {
        if (!this.props.order) return "";

        const insight_types = this.filterInsights({ exclude: ["tool_setup"], insights: this.props.insights })
            .map(insight => insight.type)
            .filter(insight_type => this.props.filtered_insight_types.includes(insight_type));

        if (insight_types.length > 0) {
            // Note - if there are both error and warning insights, return the error color
            if (isErrorInsight(insight_types)) {
                return RED_COLOR;
            }

            if (isWarningInsight(insight_types)) {
                return ORANGE_COLOR;
            }
        }

        if (ORDER_TAGS_ACCESS.cleaning_order(this.props.order.tags)) {
            return GREEN_COLOR;
        }

        return "";
    }

    getRowStyles(): React.CSSProperties {
        const backgroundColor = this.getRowBackgroundColor();
        const opacity = this.props.is_translucent ? 0.4 : 1;
        const left_border_color = this.getInsightColor();

        return {
            backgroundColor,
            opacity,
            boxShadow: left_border_color ? `inset 4px 0 0 0 ${left_border_color}` : undefined,
        } as React.CSSProperties;
    }

    renderWorkorderCell(order_gui?: OrderObjGui): React.ReactNode {
        if (!this.props.order) return;

        const external_id = order_gui ? order_gui.external_id : "";
        const process_num = order_gui ? order_gui.process_num : 0;

        const parent_external_id = this.props.order ? this.props.order.tags[ORDER_TAGS.external_parent_order_id] : "";
        const tags = this.props.order.tags;
        const is_unreleased = !ORDER_TAGS_ACCESS.system_status_released(tags);
        const priority = tags[ORDER_TAGS.priority];

        return (
            <div style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: 8 }}>
                <i className="fas fa-bullseye mr-0 icon-interactive" onClick={this.onClickHighlight}></i>
                <strong
                    style={{ display: "flex", justifyContent: "center", alignItems: "center", whiteSpace: "nowrap" }}
                >
                    {this.getFixedLine(this.props.order)} {this.getFrozen(this.props.order)} {external_id}{" "}
                    {parent_external_id ? `(P${parent_external_id})` : ""} / {process_num}
                </strong>
                {order_gui !== undefined && (
                    <OrderBadges
                        order_type={[ORDER_TYPE.plan].find(t => t === order_gui.order_type)}
                        is_unreleased={is_unreleased}
                        priority={priority}
                        has_tool_setup={this.props.has_tool_setup}
                        order_id={external_id}
                        leading_order_id={ORDER_TAGS_ACCESS.leading_order(tags)}
                    />
                )}
            </div>
        );
    }

    renderMaterialCell(order_gui?: OrderObjGui): React.ReactNode {
        if (!this.props.order) return;

        const tags = this.props.order.tags;
        const required_deadline = ORDER_TAGS_ACCESS.required_deadline(tags);

        return (
            <>
                {this.renderMaterialIdCell(order_gui)}{" "}
                <div style={{ display: "inline-block" }}> - {this.renderMaterialTitleCell(order_gui)}</div>
                {!this.props.waterfall_mode && required_deadline > 0 && (
                    <div style={{ minWidth: "inherit" }}>
                        {translate("common.deadline", "Deadline") + ": "} {this.renderMaterialDeadlineCell()}
                    </div>
                )}
                {!this.props.waterfall_mode && this.renderMaterialToolSetupCell(order_gui)}
            </>
        );
    }

    renderMaterialIdCell(order_gui?: OrderObjGui): React.ReactNode {
        const material_external_id = order_gui ? order_gui.material_external_id : "";
        return <strong>{material_external_id}</strong>;
    }

    renderMaterialTitleCell(order_gui?: OrderObjGui): React.ReactNode {
        const material_external_id = order_gui ? order_gui.material_external_id : "";
        const material_title = order_gui ? order_gui.material_title : "";
        return translatedMaterialTitle({ material_external_id, material_title: material_title });
    }

    renderMaterialDeadlineCell(): React.ReactNode {
        if (!this.props.order) return;

        const tags = this.props.order.tags;
        const required_deadline = ORDER_TAGS_ACCESS.required_deadline(tags);
        const end_time_sim = this.props.end_time_sim;

        return (
            required_deadline > 0 && (
                <>
                    {niceDateTime(new Date(required_deadline))}{" "}
                    {end_time_sim !== undefined && end_time_sim > required_deadline && (
                        <i
                            className="fas fa-exclamation-circle text-danger"
                            title={translate(
                                "Manufacturing.InsightTypesSelect.operation_exceeds_deadline",
                                "Operation exceeds deadline"
                            )}
                        />
                    )}
                </>
            )
        );
    }

    renderMaterialToolSetupCell(order_gui?: OrderObjGui): React.ReactNode {
        const material_external_id = order_gui ? order_gui.material_external_id : "";
        const tool_setup_labels_session_offset = this.props.tool_setup_labels_session_offset;
        const prev_material_id = this.props.prev_material_id;

        return tool_setup_labels_session_offset >= 0 && prev_material_id ? (
            <ToolSetupLabel
                material_external_id={material_external_id}
                prev_material_external_id={prev_material_id}
                line_external_id={this.props.line_external_id ?? ""}
            />
        ) : null;
    }

    renderUnitCell(order_gui?: OrderObjGui): React.ReactNode {
        const quantity_total_str = order_gui ? order_gui.quantity_total_str : "";
        const measurement_unit = order_gui ? order_gui.measurement_unit : "";

        return `${quantity_total_str} ${measurement_unit}`;
    }

    renderPlannedOrderEndDateCell(): React.ReactNode {
        const end_time_sim = this.props.end_time_sim;
        return end_time_sim ? niceDateTime(new Date(end_time_sim)) : "-";
    }

    renderBomAvailabilityDaysCell(): React.ReactNode {
        if (!this.props.order) return;

        const start_time_sim = this.props.start_time_sim;
        const bom_availability_date = Number(this.props.order.tags.bom_availability_date);

        if (bom_availability_date && start_time_sim) {
            const interval: moment.Duration = moment.duration(start_time_sim - bom_availability_date);
            const days_diff = Math.floor(interval.asDays());
            return String(days_diff) || "0";
        }

        return "";
    }

    renderRequiredDeadlineDiffCell(): React.ReactNode {
        if (!this.props.order) return;

        const required_deadline = Number(this.props.order.tags.required_deadline);
        const end_time_sim = this.props.end_time_sim;

        if (required_deadline && end_time_sim) {
            const interval: moment.Duration = moment.duration(required_deadline - end_time_sim);
            const days_diff = Math.floor(interval.asDays());
            return String(days_diff) || "0";
        }

        return "";
    }

    renderEditOrderCell(order_gui?: OrderObjGui): React.ReactNode {
        const order_uuid = order_gui ? order_gui.uuid : "";
        const is_readonly = this.props.is_readonly;
        const has_tool_setup_insights =
            this.filterInsights({ include: ["tool_setup"], insights: this.props.insights }).length > 0;

        return (
            <>
                {!this.props.is_unscheduled && has_tool_setup_insights && (
                    <div style={{ width: "23px" }}>
                        <i className="fas fa-tools" title={translate("common.tooltip_tool_change", "Tool change")}></i>
                    </div>
                )}
                {!is_readonly && <ConnectedEditButton order_uuid={order_uuid} />}
            </>
        );
    }

    renderMoveOrderCell(): React.ReactNode {
        const { is_readonly, is_unscheduled, dragHandleProps } = this.props;
        if (is_unscheduled || is_readonly || dragHandleProps == null) {
            return null;
        }
        return (
            <DragHandle
                dragHandleProps={dragHandleProps}
                title={translate("common.tooltip_planning_table_moveorder", "Move order")}
            />
        );
    }

    renderInlineCommentCell(order_gui?: OrderObjGui): React.ReactNode | null {
        const order_uuid = order_gui ? order_gui.uuid : "";
        const comment = this.props.order ? this.props.order.order_comment : "";

        return <ConnectedOrderInlineComment comment={comment} order_uuid={order_uuid} />;
    }

    renderCommentRow(
        column_count: number,
        row_styles: React.CSSProperties,
        onClick: (e: React.MouseEvent<HTMLTableRowElement>) => void
    ): React.ReactNode | null {
        if (this.props.columns.some(({ id }) => id === "comment_inline")) {
            return null;
        }

        const order_gui = this.state.order_gui;
        const order_uuid = order_gui ? order_gui.uuid : "";
        const comment = this.props.order ? this.props.order.order_comment : "";

        return comment ? (
            <tr style={row_styles} onClick={onClick}>
                <td colSpan={column_count} className="p-3">
                    <OrderCommentTextArea comment={comment} order_uuid={order_uuid} />
                </td>
            </tr>
        ) : null;
    }

    renderOrderDetailsComponent(order_gui?: OrderObjGui): React.ReactNode | null {
        const { sortIndex } = this.props;
        const enabled = order_gui ? order_gui.enabled : false;
        const row_class = !enabled ? "table-inactive" : "";
        const is_readonly = this.props.is_readonly;

        return order_gui ? (
            <OrderDetailsCell
                key={"editable-cell-" + sortIndex}
                row_class={row_class}
                index={sortIndex}
                selected_order={order_gui}
                handleSelectChange={this.handleSelectChange}
                line_uuid={this.props.line_uuid}
                insights={this.props.insights}
                explanation={this.props.explanation}
                line_group_uuid={this.props.line_group_uuid}
                is_unscheduled={this.props.is_unscheduled ?? false}
                is_readonly={is_readonly}
                can_ignore_earliest_start={this.props.can_ignore_earliest_start}
                start_time_sim={this.props.start_time_sim}
            />
        ) : null;
    }

    getUniqueKey(suffix: string): string {
        return [this.props.order_uuid, suffix].join("-");
    }

    /**
     * Retrieves and calls the render function associated with the specified column ID from the makeRenderCell.
     */
    renderCells(): React.ReactNode[] {
        if (!this.props.order) return [];

        return this.props.columns
            .map(({ id, min_width, align }) => {
                let renderCell = this.makeRenderCell(id);
                if (typeof renderCell !== "function") {
                    renderCell = (order_gui?: OrderObjGui) => (order_gui ? order_gui[id] : "");
                }

                const style: React.CSSProperties = {
                    minWidth: min_width || "auto",
                    textAlign: (align as TextAlign) || "auto",
                };

                const conditional_styles = ConditionalStyles.add(id, this.props.order!, {
                    start_time: this.props.start_time_sim,
                    end_time: this.props.end_time_sim,
                });
                const value = renderCell.call(this, this.state.order_gui);

                return (
                    <td key={this.getUniqueKey(id)} className={`sortable-element__${id}`} style={style}>
                        {conditional_styles ? <span style={conditional_styles}>{value}</span> : value}
                    </td>
                );
            })
            .filter(Boolean);
    }

    render() {
        // unscheduled orders that jump to lines orders are no longer existent
        const cells = this.renderCells();
        const row_styles = this.getRowStyles();

        return (
            <>
                {this.props.draw_split_line && (
                    <tr>
                        <td colSpan={this.props.columns.length} style={{ backgroundColor: "#ecebeb" }}></td>
                    </tr>
                )}
                <tr className="order-row" style={row_styles} ref={this.myRef} onClick={this.onClick}>
                    {cells}
                </tr>

                {!this.props.waterfall_mode && (
                    <>
                        {this.renderCommentRow(cells.length, row_styles, this.onClick)}
                        {this.props.is_rescheduled && this.props.order && (
                            <tr className="order-row-detailed" style={row_styles} onClick={this.onClick}>
                                <td colSpan={cells.length}>{this.renderOrderDetailsComponent(this.state.order_gui)}</td>
                            </tr>
                        )}
                    </>
                )}
            </>
        );
    }
}

Order.defaultProps = {
    enabled_edit: true,
};

const mapState = (state: ReduxState, own_props: OwnProps) => {
    const planning_table = state.gantt_chart_planning_table;
    const insights_mapped = state.gantt_chart_insights.insights_mapped;
    const filters = state.gantt_chart_filters;
    const lines_orders = state.gantt_chart_lines_orders.lines_orders;
    const unscheduled_lines_orders = state.gantt_chart_lines_orders.unscheduled_orders;
    const line_group_uuid = state.gantt_chart_properties.line_group_uuid;
    const insights = state.gantt_chart_insights.insights;
    const tool_setup_labels_session_offset = state.gantt_chart_insights.tool_setup_labels_session_offset;
    const report = state.gantt_chart_report.report_data?.result;
    const waterfall_mode = state.gantt_chart_properties.waterfall_mode;
    const filtered_insight_types = state.standalone_gantt.insight_types;

    let is_readonly = false;
    let can_ignore_earliest_start = true;
    let has_tool_setup = false;
    let order: IOrderProducedModelEx | undefined;
    let prev_material_id: string | undefined;
    let line_external_id: string | undefined;

    if (own_props.is_unscheduled) {
        for (const line_orders of unscheduled_lines_orders) {
            order = line_orders.orders.find(o => o.uuid === own_props.order_uuid);
            if (order) break;
        }
    } else {
        const line = lines_orders.find(lo => lo.line_uuid === own_props.line_uuid);
        if (line) {
            line_external_id = line.line_external_id;
            can_ignore_earliest_start = canIgnoreEarliestStart(line.tags);
            const idx = line.orders.findIndex(o => o.uuid === own_props.order_uuid);
            order = line.orders[idx];
            if (idx > 0) {
                prev_material_id = line.orders[idx - 1].material_external_id;
            }

            if (order) {
                is_readonly = LinesOrdersLogic.isOrderReadOnly(line, order);
            }
        }

        has_tool_setup =
            insights !== null &&
            insights.find(
                insight =>
                    insight.type === INSIGHT_TYPES.tool_setup &&
                    INSIGHT_TAGS_ACCESS.order_uuid(insight.tags) === own_props.order_uuid
            ) !== undefined;
    }

    let order_insights: t.IEventDataEx[] = [];

    if (insights_mapped !== null) {
        order_insights = insights_mapped.get(own_props.order_uuid) || [];
        order_insights.sort((i1, i2) =>
            i1.type === INSIGHT_TYPES.tool_setup ? -1 : i2.type === INSIGHT_TYPES.tool_setup ? 1 : i1.ts - i2.ts
        );
    }

    const explanation: t.IPlanExplanation | undefined =
        state.gantt_chart_planning_table.recommend_plan.explanations.length === 0
            ? {
                  operation_uuid: order ? order.uuid : "",
                  type: PLANNING_EXPLANATION_TYPE.no_automatic_planning,
                  possible_prod_vers: [],
                  extra_data: {},
              }
            : state.gantt_chart_planning_table.recommend_plan.explanations.find(
                  e => e.operation_uuid === (order ? order.uuid : "")
              );

    const is_rescheduled = own_props.order_uuid === planning_table.last_clicked_order_uuid;

    let is_translucent = false;
    if (planning_table.show_valid_lines.valid_orders_map !== null) {
        const valid_order = planning_table.show_valid_lines.valid_orders_map.get(own_props.order_uuid);
        if (own_props.is_unscheduled) {
            is_translucent = valid_order === undefined;
        } else if (valid_order) {
            is_translucent = valid_order.is_translucent;
        }
    }

    const is_filter_locked = filters.is_filter_locked;

    let start_time_sim: number | undefined;
    let end_time_sim: number | undefined;
    if (report) {
        const shift_time = report.from_shift_time ? dateFromShiftTime(report.from_shift_time) : undefined;

        if (shift_time && report.orders.length) {
            // Find the simulation line matching the provided line_uuid.
            const sim_line = report.orders.find(order => order.line === own_props.line_uuid);
            if (sim_line) {
                const sim_order = sim_line.production.find(order => order.order_id === own_props.order_uuid);

                if (sim_order !== undefined) {
                    const estimated_start_val = sim_order.estimated_start[1].val;
                    start_time_sim = addHours(shift_time, estimated_start_val).getTime();
                    end_time_sim = addHours(shift_time, sim_order.estimated_completion[1].val).getTime();
                }
            }
        }
    }

    return {
        should_scroll: false, // TODO
        order,
        insights: order_insights,
        show_unscheduled_orders: planning_table.show_unscheduled_orders,
        is_rescheduled: is_rescheduled,
        can_ignore_earliest_start,
        explanation,
        line_external_id,
        line_group_uuid: line_group_uuid ?? "",
        is_readonly,
        filters,
        planning_table,
        is_translucent,
        is_filter_locked,
        quantity_total: order ? order.quantity_total : 0,
        has_tool_setup,
        tool_setup_labels_session_offset,
        prev_material_id,
        start_time_sim,
        end_time_sim,
        waterfall_mode,
        highlighted_move_order_uuid: planning_table.highlighted_move_order_uuid,
        filtered_insight_types,
        report_loading: state.gantt_chart_report.report_loading,
    };
};

const mapDispatch = (dispatch: Dispatch<ActionTypes>) => ({
    reduxDispatch: dispatch,
});

type ReduxProps = ReturnType<typeof mapState> & ReturnType<typeof mapDispatch>;
export default connect<ReturnType<typeof mapState>, ReturnType<typeof mapDispatch>, OwnProps, ReduxState>(
    mapState,
    mapDispatch
)(Order);
